import React, { memo } from 'react';
import { Dialog, Paper, Box } from '@mui/material';
import { useBaseDialogController } from './BaseDialog.controller';
import { BaseDialogProps } from './BaseDialog.model';

/**
 * BaseDialog View Component
 * Pure UI component using theme tokens - NO hardcoded styles!
 * Supports both modal and embedded modes
 */
const BaseDialogViewComponent = <TData = any>(props: BaseDialogProps<TData>) => {
  const { open, children, header, footer, mode = 'modal' } = props;

  const controller = useBaseDialogController(props);

  // Content wrapper with header and footer support
  const dialogContent = (
    <>
      {header}
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {children}
      </Box>
      {footer}
    </>
  );

  // Embedded mode: render as Paper component without backdrop
  if (controller.isEmbedded) {
    return (
      <Paper {...controller.paperProps}>
        {dialogContent}
      </Paper>
    );
  }

  // Modal mode: render as full MUI Dialog
  return (
    <Dialog
      open={open}
      {...controller.dialogProps}
    >
      {dialogContent}
    </Dialog>
  );
};

/**
 * Memoized BaseDialog View Component
 * Optimized for performance with shallow comparison
 */
export const BaseDialogView = memo(BaseDialogViewComponent) as typeof BaseDialogViewComponent;

/**
 * BaseDialog Container Component
 * Main component that consumers will use
 * Provides additional wrapper functionality if needed
 */
const BaseDialogComponent = <TData = any>(props: BaseDialogProps<TData>) => {
  return <BaseDialogView {...props} />;
};

export const BaseDialog = memo(BaseDialogComponent) as typeof BaseDialogComponent;

/**
 * BaseDialog with responsive behavior
 * Automatically adjusts to mobile screens
 */
const ResponsiveBaseDialogComponent = <TData = any>(props: BaseDialogProps<TData>) => {
  const { useResponsiveDialogProps } = require('./BaseDialog.controller');
  const responsiveProps = useResponsiveDialogProps(props);

  return <BaseDialog {...responsiveProps} />;
};

export const ResponsiveBaseDialog = memo(ResponsiveBaseDialogComponent) as typeof ResponsiveBaseDialogComponent;

/**
 * BaseDialog for modal dialogs specifically
 * Ensures modal mode and provides type safety
 */
const ModalDialogComponent = <TData = any>(
  props: Omit<BaseDialogProps<TData>, 'mode'>
) => {
  return <BaseDialog {...props} mode="modal" />;
};

export const ModalDialog = memo(ModalDialogComponent) as typeof ModalDialogComponent;

/**
 * BaseDialog for embedded dialogs specifically
 * Ensures embedded mode and provides type safety
 */
const EmbeddedDialogComponent = <TData = any>(
  props: Omit<BaseDialogProps<TData>, 'mode' | 'open' | 'onClose'>
) => {
  return <BaseDialog {...props} mode="embedded" open={true} />;
};

export const EmbeddedDialog = memo(EmbeddedDialogComponent) as typeof EmbeddedDialogComponent;

// Default export
export default BaseDialog;